import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { AlertOctagon, GitBranch, RefreshCw, Rocket, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { Button, Dialog, Input, ManagedDataTable, StatisticCard } from '@/design-system';
import {
  managerConnectorService,
  type ManagerConnectorGateway,
  type SidecarRelease,
  type SidecarRollback,
} from './api/connector-gateways.service';

type ForceUpdateDialogState =
  | { kind: 'none' }
  | { kind: 'force-update'; gateway: ManagerConnectorGateway };

function describeAutoUpdate(enabled: boolean | null | undefined): string {
  if (enabled === true) return 'Ligada';
  if (enabled === false) return 'Desligada';
  return 'Não informado';
}

export function SidecarReleasesPage() {
  const qc = useQueryClient();
  const [forceUpdateDialog, setForceUpdateDialog] = useState<ForceUpdateDialogState>({
    kind: 'none',
  });
  const [forceUpdateVersion, setForceUpdateVersion] = useState('');
  const [forceUpdateReason, setForceUpdateReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceUpdateFeedback, setForceUpdateFeedback] = useState<string | null>(null);

  const releases = useQuery<SidecarRelease[]>({
    queryKey: ['manager-sidecar-releases'],
    queryFn: () => managerConnectorService.listSidecarReleases({ limit: 30 }),
    refetchInterval: 60_000,
  });

  const rollbacks = useQuery<SidecarRollback[]>({
    queryKey: ['manager-sidecar-rollbacks'],
    queryFn: () => managerConnectorService.listSidecarRollbacks(50),
    refetchInterval: 60_000,
  });

  const gateways = useQuery<ManagerConnectorGateway[]>({
    queryKey: ['manager-connector-gateways'],
    queryFn: () => managerConnectorService.listGateways(),
    refetchInterval: 30_000,
  });

  const latestStableVersion = useMemo(
    () => (releases.data ?? []).find((r) => r.channel === 'stable')?.version ?? '',
    [releases.data]
  );

  const stats = useMemo(() => {
    const rels = releases.data ?? [];
    const rolls = rollbacks.data ?? [];
    const latestStable = rels.find((r) => r.channel === 'stable');
    const latestDev = rels.find((r) => r.channel === 'dev');
    const rollbacksLast24h = rolls.filter(
      (r) => Date.now() - new Date(r.reported_at).getTime() < 24 * 3600 * 1000
    ).length;
    return {
      latestStable: latestStable?.version ?? '—',
      latestDev: latestDev?.version ?? '—',
      totalReleases: rels.length,
      rollbacks24h: rollbacksLast24h,
    };
  }, [releases.data, rollbacks.data]);

  const releaseColumns = useMemo<ColumnDef<SidecarRelease>[]>(
    () => [
      { accessorKey: 'version', header: 'Versão' },
      {
        accessorKey: 'channel',
        header: 'Canal',
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
              row.original.channel === 'stable'
                ? 'bg-green-100 text-green-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {row.original.channel}
          </span>
        ),
      },
      { accessorKey: 'adapter', header: 'Adapter', filterFn: 'equalsString' },
      {
        accessorKey: 'released_at',
        header: 'Publicada em',
        cell: ({ row }) => new Date(row.original.released_at).toLocaleString('pt-BR'),
      },
      {
        accessorKey: 'pinned_gateway_count',
        header: 'Canary pins',
        cell: ({ row }) => (row.original.pinned_gateway_count > 0 ? row.original.pinned_gateway_count : '—'),
      },
      {
        accessorKey: 'release_notes',
        header: 'Notas',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600 line-clamp-2">{row.original.release_notes ?? '—'}</span>
        ),
      },
    ],
    []
  );

  const rollbackColumns = useMemo<ColumnDef<SidecarRollback>[]>(
    () => [
      {
        accessorKey: 'reported_at',
        header: 'Reportado em',
        cell: ({ row }) => new Date(row.original.reported_at).toLocaleString('pt-BR'),
      },
      { accessorKey: 'gateway_id', header: 'Gateway' },
      {
        accessorKey: 'from_version',
        header: 'De → Para',
        cell: ({ row }) => `${row.original.from_version} → ${row.original.to_version}`,
      },
      {
        accessorKey: 'reason',
        header: 'Razão',
        cell: ({ row }) => (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs ${
              row.original.reason === 'crashloop'
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {row.original.reason}
          </span>
        ),
      },
      {
        accessorKey: 'crashes_in_window',
        header: 'Crashes',
        cell: ({ row }) => row.original.crashes_in_window ?? '—',
      },
    ],
    []
  );

  const gatewayColumns = useMemo<ColumnDef<ManagerConnectorGateway>[]>(
    () => [
      { accessorKey: 'companyName', header: 'Empresa', size: 180, cell: ({ row }) => <span className="block w-40 truncate" title={row.original.companyName}>{row.original.companyName}</span> },
      { accessorKey: 'name', header: 'Gateway' },
      { accessorKey: 'connectorVersion', header: 'Sidecar', cell: ({ row }) => row.original.connectorVersion ?? '—' },
      { accessorKey: 'shellVersion', header: 'Shell', cell: ({ row }) => row.original.shellVersion ?? '—' },
      { accessorKey: 'effectiveStatus', header: 'Status', filterFn: 'equalsString' },
      { accessorKey: 'adapterId', header: 'Adapter', filterFn: 'equalsString' },
      { accessorKey: 'releaseChannel', header: 'Canal', filterFn: 'equalsString' },
      {
        id: 'autoUpdate',
        header: 'Atualização automática',
        cell: ({ row }) => describeAutoUpdate(row.original.autoUpdateEnabled),
      },
      {
        id: 'updateStatus',
        header: 'Atualização',
        cell: ({ row }) => row.original.pendingForceUpdateVersion
          ? `Aguardando aplicação de ${row.original.pendingForceUpdateVersion}`
          : '—',
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setForceUpdateFeedback(null);
                setForceUpdateDialog({ kind: 'force-update', gateway: row.original });
                setForceUpdateVersion(latestStableVersion);
                setForceUpdateReason('');
              }}
            >
              <Zap className="h-3 w-3 mr-1" />
              Forçar update
            </Button>
            {row.original.pendingForceUpdateVersion && (
              <Button size="sm" variant="ghost" onClick={() => handleCancelForceUpdate(row.original)}>
                Cancelar pedido
              </Button>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latestStableVersion]
  );

  async function handleCancelForceUpdate(gateway: ManagerConnectorGateway) {
    try {
      await managerConnectorService.clearForceUpdate(gateway.id);
      setForceUpdateFeedback('Pedido de atualização cancelado.');
      qc.invalidateQueries({ queryKey: ['manager-connector-gateways'] });
    } catch (e) {
      console.error('clear force update failed', e);
      setForceUpdateFeedback('Não foi possível cancelar o pedido. Tente novamente.');
    }
  }

  async function handleForceUpdate() {
    if (forceUpdateDialog.kind !== 'force-update') return;
    setIsSubmitting(true);
    try {
      await managerConnectorService.forceUpdateGateway(
        forceUpdateDialog.gateway.id,
        forceUpdateVersion,
        forceUpdateReason || undefined
      );
      setForceUpdateFeedback('Solicitação registrada. O Manager mostrará a versão aplicada após o próximo heartbeat do conector.');
      setForceUpdateDialog({ kind: 'none' });
      qc.invalidateQueries({ queryKey: ['manager-connector-gateways'] });
    } catch (e) {
      console.error('force update failed', e);
      const unknownVersion =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error === 'unknown_version';
      setForceUpdateFeedback(
        unknownVersion
          ? `A versão ${forceUpdateVersion} não existe entre as releases publicadas.`
          : 'Não foi possível solicitar a atualização. Tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DashboardLayout title="Releases do Sidecar">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatisticCard appearance="highlight" loading={releases.isLoading} icon={Rocket} tone="primary" title="Última stable" value={stats.latestStable} />
          <StatisticCard appearance="highlight" loading={releases.isLoading} icon={GitBranch} tone="warning" title="Última dev" value={stats.latestDev} />
          <StatisticCard appearance="highlight" loading={releases.isLoading} icon={Rocket} tone="success" title="Releases totais" value={String(stats.totalReleases)} />
          <StatisticCard appearance="highlight" loading={rollbacks.isLoading}
            icon={AlertOctagon}
            tone={stats.rollbacks24h > 0 ? 'warning' : 'neutral'}
            title="Rollbacks (24h)"
            value={String(stats.rollbacks24h)}
          />
        </div>

        <section className="space-y-2">
          <ManagedDataTable
            title="Releases publicadas"
            subtitle="Consulte as versões publicadas, seus canais de distribuição e notas de release."
            toolbarButtons={[{ label: 'Atualizar', icon: <RefreshCw />, variant: 'subtle', onClick: () => { void releases.refetch(); void rollbacks.refetch(); void gateways.refetch(); } }]}
            columnFilters={[
              { columnId: 'adapter', label: 'Adapter', options: [...new Set((releases.data ?? []).map((r) => r.adapter))].sort().map((value) => ({ value, label: value })) },
              { columnId: 'channel', label: 'Canal', options: [...new Set((releases.data ?? []).map((r) => r.channel))].sort().map((value) => ({ value, label: value })) },
            ]}
            columns={releaseColumns}
            data={releases.data ?? []}
            loading={releases.isLoading}
            emptyMessage="Nenhuma release publicada ainda. Tag sidecar-v* ou dev-v* dispara workflow no orya-connector."
          />
        </section>

        <section className="space-y-2">
          <ManagedDataTable
            title="Rollbacks automáticos"
            subtitle="Histórico de reversões de versão reportadas pelos conectores."
            columns={rollbackColumns}
            data={rollbacks.data ?? []}
            loading={rollbacks.isLoading}
            emptyMessage="Nenhum rollback reportado. Bom sinal — sem crashloop em prod."
          />
        </section>

        <section className="space-y-2">
          {forceUpdateFeedback && forceUpdateDialog.kind === 'none' && <p className="text-sm text-gray-700" role="status">{forceUpdateFeedback}</p>}
          <p className="text-sm text-gray-600">
            "Forçar update" registra a versão alvo; o conector consulta o pedido a cada ~5 minutos
            e aplica quando estiver online. Em builds que já suportam modo "somente forçado", o
            pedido é aplicado mesmo com a atualização automática desligada; em builds antigas com
            ela desligada o pedido fica pendente até o conector ser atualizado manualmente.
          </p>
          <ManagedDataTable
            title="Atualização de gateways"
            subtitle="Defina a versão alvo dos conectores. Gateways offline aplicarão o pedido quando voltarem."
            toolbarButtons={[{ label: 'Atualizar', icon: <RefreshCw />, variant: 'subtle', onClick: () => { void gateways.refetch(); } }]}
            columnFilters={[
              { columnId: 'adapterId', label: 'Adapter', options: [...new Set((gateways.data ?? []).map((g) => g.adapterId))].sort().map((value) => ({ value, label: value })) },
              { columnId: 'releaseChannel', label: 'Canal', options: [...new Set((gateways.data ?? []).map((g) => g.releaseChannel))].sort().map((value) => ({ value, label: value })) },
              { columnId: 'effectiveStatus', label: 'Status', options: [...new Set((gateways.data ?? []).map((g) => g.effectiveStatus))].sort().map((value) => ({ value, label: value })) },
            ]}
            columns={gatewayColumns}
            data={gateways.data ?? []}
            loading={gateways.isLoading}
            emptyMessage="Sem gateways pareados ainda."
          />
        </section>
      </div>

      <Dialog
        isOpen={forceUpdateDialog.kind === 'force-update'}
        onClose={() => setForceUpdateDialog({ kind: 'none' })}
        title="Forçar update do sidecar"
        footer={forceUpdateDialog.kind === 'force-update' ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setForceUpdateDialog({ kind: 'none' })}>Cancelar</Button>
            <Button variant="primary" onClick={handleForceUpdate} disabled={!forceUpdateVersion.trim() || isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Forçar update'}
            </Button>
          </div>
        ) : undefined}
      >
        {forceUpdateDialog.kind === 'force-update' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Gateway: <strong>{forceUpdateDialog.gateway.name}</strong> ({forceUpdateDialog.gateway.companyName})
            </p>
            <p className="text-sm text-gray-600">
              Versão informada pelo conector: <code>{forceUpdateDialog.gateway.connectorVersion ?? '—'}</code>
            </p>
            {forceUpdateDialog.gateway.autoUpdateEnabled === false && (
              <p className="text-sm text-amber-700" role="alert">
                Atualização automática desligada neste conector. O pedido só é aplicado se a build
                instalada suportar o modo "somente forçado".
              </p>
            )}
            <Input
              label="Versão alvo"
              placeholder="0.3.11"
              value={forceUpdateVersion}
              onChange={(e) => setForceUpdateVersion(e.target.value)}
            />
            {forceUpdateFeedback && <p className="text-sm text-red-700" role="status">{forceUpdateFeedback}</p>}
            <Input
              label="Motivo (opcional)"
              placeholder="ex: hotfix urgente"
              value={forceUpdateReason}
              onChange={(e) => setForceUpdateReason(e.target.value)}
            />
          </div>
        )}
      </Dialog>
    </DashboardLayout>
  );
}
